import { FC } from 'react';

import { AnalyticsDayLabels } from './analyticsDay.types';
import { DistributionStats } from './analyticsDistribution.util';

//* 일별 분포 한 줄 요약 — 달력 월 헤더와 추이 차트가 공용으로 쓴다.
//* 총계만 보면 "906건이 고르게 나온 건지 하루에 몰린 건지"를 알 수 없어서 붙였다.

/** 'YYYY-MM-DD' → 'M/D' */
const shortDate = (date: string) =>
  `${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))}`;

/** 변동계수 → 사람 말. 하루하루가 얼마나 들쭉날쭉한지 */
const spreadLabel = (cv: number | null): string => {
  if (cv === null) return '';
  if (cv < 0.15) return '고른 편';
  if (cv < 0.35) return '보통';
  return '들쭉날쭉';
};

const Item: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <span className="whitespace-nowrap">
    <span className="text-gray-500">{label}</span>{' '}
    <span className="font-semibold tabular-nums text-gray-800">{children}</span>
  </span>
);

export const DistributionRow: FC<{
  stats: DistributionStats;
  labels: AnalyticsDayLabels;
  /** 큰 수를 줄여 쓰는 포맷터 (달력의 compact 등) */
  format: (value: number) => string;
}> = function DistributionRow({ stats, labels, format }) {
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-gray-50 px-3 py-2 text-13">
      <span className="text-12 font-semibold text-gray-600">일별 분포</span>

      <Item label="평균">{format(Math.round(stats.mean))}</Item>
      {/* 평균과 중앙값이 벌어지면 특정 날에 몰렸다는 뜻 */}
      <Item label="중앙값">{format(Math.round(stats.median))}</Item>
      <Item label="최저">
        {format(stats.min.value)}
        <span className="ml-1 font-normal text-gray-500">
          {shortDate(stats.min.date)}
        </span>
      </Item>
      <Item label="최다">
        {format(stats.max.value)}
        <span className="ml-1 font-normal text-gray-500">
          {shortDate(stats.max.date)}
        </span>
      </Item>
      <Item label="편차">
        ±{format(Math.round(stats.stdDev))}
        {stats.cv !== null && (
          <span className="ml-1 font-normal text-gray-500">
            {(stats.cv * 100).toFixed(0)}% · {spreadLabel(stats.cv)}
          </span>
        )}
      </Item>

      <span className="ml-auto text-12 text-gray-500">
        {labels.value} 있던 날 {stats.count}일 기준
      </span>
    </div>
  );
};
